import { formatInTimeZone, fromZonedTime, toZonedTime } from "date-fns-tz";
import { addDays, subDays } from "date-fns";

/**
 * Calculates streak numbers for days in a weekly view.
 * 
 * Logic:
 * 1. Count back from current streak until 0 (for the most recent worked days)
 * 2. For older worked days (before current streak), start with longest streak and count back
 * 
 * Example: currentStreak=2, longestStreak=59
 * - Last worked day: 2
 * - Previous worked day: 1
 * - Next older worked day: 59 (starts at longestStreak)
 * - Previous older worked day: 58 (longestStreak - 1)
 * 
 * @param days - Array of day objects with an index and a way to check if worked
 * @param currentStreak - Current streak count
 * @param longestStreak - Longest streak count
 * @param isWorked - Function to check if a day is "worked" (has activity)
 * @returns Array of streak numbers (null for non-worked days, number for worked days)
 */
export function calculateStreakNumbers<T extends { index?: number }>(
  days: T[],
  currentStreak: number,
  longestStreak: number,
  isWorked: (day: T) => boolean
): Array<number | null> {
  // First, identify all worked days and their indices
  const workedDayIndices: number[] = [];
  days.forEach((day, idx) => {
    const dayIndex = day.index !== undefined ? day.index : idx;
    if (isWorked(day)) {
      workedDayIndices.push(dayIndex);
    }
  });

  if (workedDayIndices.length === 0) {
    return days.map(() => null);
  }

  // Sort worked day indices to ensure proper ordering
  workedDayIndices.sort((a, b) => a - b);

  return days.map((day, idx) => {
    const dayIndex = day.index !== undefined ? day.index : idx;
    const worked = isWorked(day);

    if (!worked) {
      return null;
    }

    // Find position of this day among worked days (from end, 0-indexed)
    const workedDayPosition = workedDayIndices.indexOf(dayIndex);
    const positionFromEnd = workedDayIndices.length - 1 - workedDayPosition;

    let streakNumber: number | null = null;

    // Count back from current streak first
    if (currentStreak > 0 && positionFromEnd < currentStreak) {
      // This day is within the current streak range
      // Last worked day (positionFromEnd=0) gets currentStreak
      // Previous worked day (positionFromEnd=1) gets currentStreak-1, etc.
      streakNumber = currentStreak - positionFromEnd;
    } else if (longestStreak > 0) {
      // This day is before the current streak ends, use longest streak
      // Count how many worked days are in the current streak
      const workedDaysInCurrentStreak = Math.min(currentStreak, workedDayIndices.length);
      
      // Count how many older worked days come BEFORE this one (further from current streak)
      // We want the closest older worked day to current streak to get longestStreak
      // So we count how many older worked days are further back (have lower indices)
      const currentStreakStartPosition = workedDayIndices.length - workedDaysInCurrentStreak;
      const workedDaysBeforeThis = workedDayIndices
        .filter((_, pos) => pos < workedDayPosition && pos < currentStreakStartPosition)
        .length;
      
      // Count back from longest streak
      // The first older worked day (closest to current streak, highest index) gets longestStreak
      // The next older worked day (further back, lower index) gets longestStreak - 1, etc.
      // So we subtract the count of days before this one
      const totalOlderWorkedDays = currentStreakStartPosition;
      streakNumber = longestStreak - (totalOlderWorkedDays - workedDaysBeforeThis - 1);
    }

    // Fallback: count worked days in the window
    if (streakNumber === null || streakNumber <= 0) {
      streakNumber = positionFromEnd + 1;
    }

    return streakNumber;
  });
}

/**
 * Calculates the Sunday-Saturday week containing a target date.
 * @param targetDate - Date string in YYYY-MM-DD format, or Date object. Defaults to today.
 * @param timezone - User's timezone (e.g., "America/New_York"). Defaults to "UTC".
 * @returns Object with sundayDate (Date), saturdayDate (string YYYY-MM-DD), and todayStr (string YYYY-MM-DD)
 */
export function calculateWeekDates(targetDate?: string | Date, timezone: string = "UTC"): {
  sundayDate: Date;
  saturdayDate: string;
  todayStr: string;
} {
  // Get today's date in the user's timezone (YYYY-MM-DD format) using date-fns-tz
  const now = new Date();
  const todayStr = formatInTimeZone(now, timezone, 'yyyy-MM-dd');
  
  // Determine target date in user's timezone
  let targetDateStr: string;
  if (targetDate instanceof Date) {
    targetDateStr = formatInTimeZone(targetDate, timezone, 'yyyy-MM-dd');
  } else if (typeof targetDate === "string") {
    targetDateStr = targetDate; // Already in YYYY-MM-DD format
  } else {
    targetDateStr = todayStr; // Use today
  }
  
  // Create a date object at midnight in the user's timezone for the target date
  // Parse the date string components and create a date in the user's timezone
  const [year, month, day] = targetDateStr.split('-').map(Number);
  // Create a date representing midnight in the user's timezone
  const targetMidnightInTz = new Date(year, month - 1, day, 0, 0, 0, 0);
  // Convert to UTC (this gives us the UTC time that represents midnight in the user's timezone)
  const targetInUtc = fromZonedTime(targetMidnightInTz, timezone);
  
  // Calculate Sunday of the week containing the target date in the user's timezone
  // We need to find Sunday at midnight in the user's timezone, not UTC
  // Get the date in the user's timezone to find the day of week
  const targetInUserTz = toZonedTime(targetInUtc, timezone);
  // Get day of week (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
  const dayOfWeek = targetInUserTz.getDay();
  // Calculate how many days to subtract to get to Sunday
  const daysToSunday = dayOfWeek;
  // Calculate Sunday by subtracting days in the user's timezone
  const sundayDateStr = formatInTimeZone(subDays(targetInUtc, daysToSunday), timezone, 'yyyy-MM-dd');
  // Parse Sunday date components and create Sunday at midnight in user's timezone
  const [sundayYear, sundayMonth, sundayDay] = sundayDateStr.split('-').map(Number);
  const sundayMidnightInTz = new Date(sundayYear, sundayMonth - 1, sundayDay, 0, 0, 0, 0);
  const sundayDate = fromZonedTime(sundayMidnightInTz, timezone);
  
  // Calculate Saturday (6 days after Sunday) in the user's timezone
  const saturdayInUtc = addDays(sundayDate, 6);
  
  // Format Saturday date in user's timezone
  const saturdayStr = formatInTimeZone(saturdayInUtc, timezone, 'yyyy-MM-dd');
  
  // Return sundayDate which is a Date object that represents Sunday at midnight in the user's timezone
  // When we add days to it and format in the user's timezone, we get the correct dates
  return { sundayDate, saturdayDate: saturdayStr, todayStr };
}

/**
 * Normalizes activity streak data into a continuous series for a week (Sunday through Saturday).
 * @param rawDays - Array of day objects from the API with date and sessionCount
 * @param sundayDate - The Sunday date of the week
 * @param days - Number of days to normalize (default 7)
 * @returns Array of normalized day objects with date and sessionCount
 */
export function normalizeWeekDays(
  rawDays: Array<{ date: string | Date; sessionCount?: number | null }>,
  sundayDate: Date,
  days: number = 7
): Array<{ date: string; sessionCount: number | null }> {
  // Build lookup map from raw data
  const lookup = new Map<string, number | null>();
  for (const d of rawDays) {
    const dateStr = typeof d.date === 'string' ? d.date : new Date(d.date).toISOString().split("T")[0];
    lookup.set(dateStr, d.sessionCount ?? null);
  }
  
  // Build normalized array for the week
  const normalizedDays: Array<{ date: string; sessionCount: number | null }> = [];
  for (let i = 0; i < days; i++) {
    const dt = new Date(sundayDate);
    dt.setUTCDate(sundayDate.getUTCDate() + i);
    const dateStr = dt.toISOString().slice(0, 10);
    const sessionCount = lookup.has(dateStr) ? (lookup.get(dateStr) ?? null) : null;
    normalizedDays.push({ date: dateStr, sessionCount });
  }
  
  return normalizedDays;
}

/**
 * Prepares weekly streak data for rendering.
 * Combines normalization, streak number calculation, and day metadata.
 * @param rawDays - Array of day objects from the API
 * @param sundayDate - The Sunday date of the week
 * @param todayStr - Today's date string (YYYY-MM-DD)
 * @param currentStreak - Current streak count
 * @param longestStreak - Longest streak count
 * @param days - Number of days (default 7)
 * @returns Array of day objects ready for rendering
 */
export function prepareWeeklyStreakData(
  rawDays: Array<{ date: string | Date; sessionCount?: number | null }>,
  sundayDate: Date,
  todayStr: string,
  currentStreak: number,
  longestStreak: number,
  days: number = 7
): Array<{
  date: string;
  sessionCount: number;
  index: number;
  isToday: boolean;
  worked: boolean;
  streakNumber: number | null;
}> {
  const normalizedDays = normalizeWeekDays(rawDays, sundayDate, days);
  
  // Calculate streak numbers using shared utility
  const streakNumbers = calculateStreakNumbers(
    normalizedDays.map((day, idx) => ({ ...day, index: idx })),
    currentStreak,
    longestStreak,
    (day) => (day.sessionCount ?? 0) > 0
  );
  
  return normalizedDays.map(({ date, sessionCount }, index) => {
    const isToday = date === todayStr;
    const worked = (sessionCount ?? 0) > 0;
    const streakNumber = streakNumbers[index];
    
    return {
      date,
      sessionCount: sessionCount ?? 0,
      index,
      isToday,
      worked,
      streakNumber,
    };
  });
}

